import React, { useCallback, useEffect, useRef } from 'react';
import gql from 'graphql-tag';
import { css } from "@emotion/react";
import { Link, useNavigate } from "react-router-dom";

import Button from "../Button";
import FormActions from "../form/FormActions";
import { useMutation } from "@apollo/react-hooks";
import { useAppState } from "../../contexts/AppContext";
import VerificationInput from "react-verification-input";
import OutsideClickHandler from "react-outside-click-handler";
import { AUTH_URL, getUrl, PAYMENT_URL } from "../../includes/pricingAuthRedirects";
import Loading from "../utils/Loading";
import theme from "../../../theme";
import errorParser from "../../includes/errorParser";
import useSetState from "../../hooks/useSetState";
import useRedirectHomeIfOnRestrictedPage from "../../hooks/useRedirectHomeIfOnRestrictedPage";
import { addPunctuationMark } from "../../includes/string";

type DefaultState = {
    isSubmitting: boolean,
    isLoadingResend: boolean,
    disableButton: boolean,
    verificationCode: number,
    verificationError: string,
};

const INITIAL_STATE = {
    isSubmitting: false,
    isLoadingResend: false,
    disableButton: false,
    verificationCode: 0,
    verificationError: '',
};

const verificationCodeLength = 5;
const VERIFICATION_CODE_PLACEHOLDER = "-";

const EmailVerification = () => {
    const navigate: any = useNavigate();
    const [appState, setAppState] = useAppState();
    const inputRef = useRef<HTMLInputElement>();
    const [state, setState] = useSetState<DefaultState>(INITIAL_STATE);
    const [verifyEmailHomeUser] = useMutation(EMAIL_VERIFICATION_QUERY);
    const [resendEmailVerification] = useMutation(RESEND_EMAIL_VERIFICATION_QUERY);
    useRedirectHomeIfOnRestrictedPage();

    const updateVerificationInputStyle = useCallback(() => {
        const characters = document.querySelectorAll(".verification-character");
        characters.forEach(character => {
            if (character.textContent === VERIFICATION_CODE_PLACEHOLDER && !character.classList.contains("verification-character--selected")) {
                character.classList.add("verification-character--inactive");
            }
        });
    }, []);

    useEffect(() => {
        setState({ disableButton: state.verificationCode.toString().length !== verificationCodeLength });
    }, [state.verificationCode, setState]);

    useEffect(() => {
        updateVerificationInputStyle();
    }, [appState.hasUserBeenLoaded, updateVerificationInputStyle]);

    const focusInput = useCallback(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    const setInputRef = useCallback((ref: HTMLInputElement) => {
        if (ref) {
            inputRef.current = ref;
        }
        focusInput();
    }, [focusInput]);

    const onSuccessVerification = useCallback(() => {
        appState.refetchUser().then(() => {
            navigate(getUrl(PAYMENT_URL));
        });
    }, [appState, navigate]);

    const submitEmailVerificationForm = useCallback(() => {
        setState({ isSubmitting: true, verificationError: '' });
        return verifyEmailHomeUser({
            variables: {
                verificationCode: state.verificationCode
            }
        }).then((data) => {
            setState({ isSubmitted: false })
            onSuccessVerification();
        }).catch((error) => {
            setState({ isSubmitting: false, verificationError: addPunctuationMark(errorParser(error)) });
        }).finally(focusInput);
    }, [state.verificationCode, verifyEmailHomeUser, onSuccessVerification, setState, focusInput]);

    const onResendEmailVerification = useCallback((e: any) => {
        e.preventDefault();
        setState({ isLoadingResend: true, verificationError: '' });

        return resendEmailVerification().then((data) => {
            setState({ isLoadingResend: false });
        }).catch((error) => {
            setState({ isLoadingResend: false, verificationError: addPunctuationMark(errorParser(error)) });
        }).finally(focusInput);
    }, [resendEmailVerification, setState, focusInput]);

    const onChange = (e: any) => {
        setState({ verificationCode: e, verificationError: '' });
    }
    useEffect(() => {
        const fn = (event: KeyboardEvent) => {
            if (event.key === 'Enter' && !state.disableButton) {
                submitEmailVerificationForm();
            }
        };

        window.addEventListener("keypress", fn);

        return () => {
            window.removeEventListener("keypress", fn);
        }
    }, [state.disableButton, submitEmailVerificationForm]);

    const userEmail = appState?.user?.Email;

    const onChangeEmailRedirect = () => {
        setAppState({
            isChangingEmail: true,
        })
    }

    if (!appState.hasUserBeenLoaded) {
        return <Loading />;
    }

    const contentStyle = css`
        width: 100%;
        display: flex;
        justify-content: center;
    `;

    const emailVerificationContent = css`
        margin-top: 20px;
        width: 100%;
        position: relative;

        ${theme.breakpoints.down("sm")} {
            width: 100%;

            .verification-characters .verification-input__character.verification-character {
                margin-right: 15px;
            }
        }
    `;

    const emailVerificationHeader = css`
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        background-color: ${theme.colours.grey['90']};
        width: 100%;
        height: 48px;
        border-radius: 24px;

        span {
            font-size: 25px;
            line-height: 30px;
            margin-left: 20px;
            font-weight: 400;
        }
    `;

    const emailVerificationText = css`
        display: flex;
        flex-direction: column;
        margin-top: 20px;
        margin-bottom: 2px;
        padding: 0 20px;

        span {
            font-size: 18px;
        }
    `;

    const emailVerificationError = (isVisible: boolean) => css`
        height: 14px;
        color: ${isVisible ? theme.colours.red[300] : 'transparent'};
        width: 100%;
        margin-top: 8px;
    `;

    const emailVerificationForm = (errorMessageVisible: boolean) => css`
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: center;
        margin: 45px 0 40px;

        .vi__character {
            border-radius: 8px;
            height: 56px;
            width: 56px;
            border: 1px solid ${theme.colours.grey['650']};

            ${errorMessageVisible && css`
                border: 1px solid ${theme.colours.crimson};
                background-color: ${theme.colours.soapStone};
            `}
        }

        ${theme.breakpoints.down('xxs')} {
            justify-content: center;
        }
    `;

    const resendCodeText = css`
        display: flex;
        margin-top: 15px;
        text-align: center;
        justify-content: center;

        span {
            font-size: 14px;
            font-weight: 300;

            a {
                text-decoration: none;
                color: ${theme.colours.blue['400']};
                font-weight: bold;

                &:hover {
                    text-decoration: underline;
                }
            }
        }
    `;

    const linkStyle = css`
        color: ${theme.colours.blue['400']};
        font-weight: bold !important;
        cursor: pointer;
        
        :hover {
            text-decoration: underline;
        }
    `;

    const formActionsStyle = css`
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: center;
        margin-top: 45px;

        ${theme.breakpoints.down('sm')} {
            margin-top: 20px;
            justify-content: center;
        }
    `;

    return (
        <div css={contentStyle}>
            <div css={emailVerificationContent}>
                {(state.isSubmitting || state.isLoadingResend) && (
                    <Loading onTop overlay overlayColour={"transparent"} />
                )}
                <div css={emailVerificationHeader}>
                    <span>Email Verification</span>
                </div>
                <div css={emailVerificationText}>
                    <span>We have sent a verification code to <b>{userEmail}</b>, please check your inbox and enter the code here.</span>
                </div>
                <div css={emailVerificationForm(!!state.verificationError)}>
                    <OutsideClickHandler onOutsideClick={updateVerificationInputStyle}>
                        <VerificationInput
                            length={5}
                            validChars="0-9"
                            placeholder={VERIFICATION_CODE_PLACEHOLDER}
                            removeDefaultStyles
                            container={{ className: "verification-container" }}
                            characters={{ className: "verification-characters" }}
                            character={{
                                className: "verification-character",
                                classNameInactive: "verification-character--inactive",
                                classNameSelected: "verification-character--selected"
                            }}
                            getInputRef={setInputRef}
                            onChange={onChange}
                        />
                        <div css={emailVerificationError(!!state.verificationError)}>
                            <span>{state.verificationError}</span>
                        </div>
                    </OutsideClickHandler>
                </div>
                <div css={resendCodeText}>
                    <span css={linkStyle} onClick={onResendEmailVerification}>Click here</span>
                    <span>&nbsp;for a new code if it didn't work or you didn't receive an email.</span>
                </div>
                <div css={resendCodeText}>
                    <span>
                        <Link to={getUrl(AUTH_URL)} onClick={onChangeEmailRedirect}>Click here</Link>
                        {` to change your email if your email is incorrect.`}
                    </span>
                </div>
                <div css={formActionsStyle}>
                    <FormActions>
                        <Button
                            type="submit"
                            color={"secondary"}
                            disabled={state.disableButton}
                            forceLoading={state.isSubmitting}
                            onClick={submitEmailVerificationForm}
                            rounded
                        >
                            Continue
                        </Button>
                    </FormActions>
                </div>
            </div>
        </div>
    );
};

const EMAIL_VERIFICATION_QUERY: any = gql`
    mutation verifyEmailHomeUser($verificationCode: Int!) {
        verifyEmailHomeUser(VerificationCode: $verificationCode) {
            id
        }
    }
`;

const RESEND_EMAIL_VERIFICATION_QUERY: any = gql`
    mutation resendSignUpEmailVerificationCode {
        resendSignUpEmailVerificationCode
    }
`;


export default EmailVerification;
